import { useEffect, useState } from 'react';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { useDispatch, useReduxState } from 'hooks';
import { useThemeEditorContext } from 'components/app/ThemeEditorContext';
import { Button, LoadingIndicator } from 'components/common';
import {
  SiteSettingsThemesEditorReader,
  SiteSettingsThemesEditorSelector,
  SiteSettingsThemesEditorSite
} from '.';

import type { ReaderTheme, Theme } from 'shared-types';

export interface SiteTheme {
  id: number;
  title: string;
  type: 'site';
  data: Theme;
  createdAt: string;
  updatedAt: string;
}

export interface SiteThemeReader {
  id: number;
  title: string;
  type: 'reader';
  data: ReaderTheme;
  createdAt: string;
  updatedAt: string;
}

const SiteSettingsThemesEditor = () => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig, theme } = useReduxState();

  const { setThemeEditorWidth, themeEditorWidth } = useThemeEditorContext();

  const { customThemeId, readerCustomThemeId } = serverConfig;

  const [loading, setLoading] = useState<boolean>(false);
  const [mode, setMode] = useState<'site' | 'reader'>('site');

  const {
    data: siteThemesData,
    loading: siteThemesLoading,
    updateData: updateSiteThemesData
  } = useApiData({
    url: '/admin/server-themes?type=site'
  });

  const {
    data: readerThemesData,
    loading: readerThemesLoading,
    updateData: updateReaderThemesData
  } = useApiData({
    url: '/admin/server-themes?type=reader'
  });

  const siteThemes: SiteTheme[] = (siteThemesData?.themes || []).sort((a, b) =>
    a.updatedAt > b.updatedAt ? -1 : 1
  );

  const activeSiteTheme = siteThemes.find(
    (theme) => theme.id === customThemeId
  );

  const readerThemes: SiteThemeReader[] = (readerThemesData?.themes || []).sort(
    (a, b) => (a.updatedAt > b.updatedAt ? -1 : 1)
  );

  const activeReaderTheme = readerThemes.find(
    (theme) => theme.id === readerCustomThemeId
  );

  const handleSave = async () => {
    if (mode === 'site') {
      await updateSiteThemesData({ showLoading: true });
    } else {
      await updateReaderThemesData({ showLoading: true });
    }
  };

  const handleThemeChange = (themeId) => {
    setLoading(true);
    if (mode === 'site') {
      if (themeId) {
        const siteTheme = siteThemes.find((theme) => theme.id === themeId);
        dispatch(actions.updateTheme(siteTheme.id, siteTheme.data));
      } else {
        dispatch(actions.updateTheme(null, {}));
      }
    } else {
      if (themeId) {
        const readerTheme = readerThemes.find((theme) => theme.id === themeId);
        dispatch(
          actions.updateReaderTheme(readerTheme.id, {
            id: readerTheme.id,
            title: readerTheme.title,
            ...readerTheme.data
          })
        );
      } else {
        dispatch(actions.updateReaderTheme(null, {}));
      }
    }
    setTimeout(() => setLoading(false));
  };

  useEffect(() => {
    if (!siteThemesLoading && customThemeId && mode === 'site') {
      handleThemeChange(customThemeId);
    }
  }, [siteThemesLoading]);

  useEffect(() => {
    if (!readerThemesLoading && readerCustomThemeId && mode === 'reader') {
      handleThemeChange(readerCustomThemeId);
    }
  }, [readerThemesLoading]);

  return (
    <div className="editor" style={{ width: themeEditorWidth }}>
      <div className="header">
        <div className="header-mode">
          <button
            className={`mode-button ${mode === 'site' ? 'active' : ''}`}
            onClick={() => setMode('site')}
            type="button"
          >
            Site theme
          </button>
          <button
            className={`mode-button ${mode === 'reader' ? 'active' : ''}`}
            onClick={() => setMode('reader')}
            type="button"
          >
            Reader theme
          </button>
        </div>
        <div className="header-size">
          <Button
            category="light"
            icon={themeEditorWidth === 300 ? 'chevron-right' : 'chevron-left'}
            onClick={() =>
              setThemeEditorWidth(themeEditorWidth === 300 ? 900 : 300)
            }
            size="header"
          />
        </div>
      </div>

      <div className="theme-selector">
        {mode === 'site' ? (
          <SiteSettingsThemesEditorSelector
            initialValue={customThemeId || 0}
            onChange={handleThemeChange}
            themeOptions={[
              {
                label: messages.commonDefault,
                value: 0
              },
              ...siteThemes.map((siteTheme) => ({
                label: siteTheme.title,
                value: siteTheme.id
              }))
            ]}
          />
        ) : (
          <SiteSettingsThemesEditorSelector
            initialValue={readerCustomThemeId || 0}
            onChange={handleThemeChange}
            themeOptions={[
              {
                label: messages.siteReaderThemesCustomNone,
                value: 0
              },
              ...readerThemes.map((readerTheme) => ({
                label: readerTheme.title,
                value: readerTheme.id
              }))
            ]}
          />
        )}
      </div>

      <div className="content">
        {loading ? (
          <LoadingIndicator />
        ) : mode === 'site' && customThemeId ? (
          <SiteSettingsThemesEditorSite
            onSave={handleSave}
            siteTheme={{
              ...activeSiteTheme?.data
            }}
            siteThemeId={customThemeId}
            title={activeSiteTheme?.title}
          />
        ) : mode === 'reader' && readerCustomThemeId ? (
          <SiteSettingsThemesEditorReader
            onSave={handleSave}
            readerTheme={{
              ...activeReaderTheme?.data
            }}
            readerThemeId={readerCustomThemeId}
            title={activeReaderTheme?.title}
          />
        ) : null}
      </div>

      <style jsx>{`
        .editor {
          background-color: white;
          border-right: 1px solid #abc;
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 1;
        }
        .header {
          border-bottom: 1px solid ${theme.lightGrey};
          display: flex;
          gap: 10px;
          padding: 10px 15px;
        }
        .header-mode {
          display: flex;
          flex-grow: 1;
          gap: 10px;
        }
        .mode-button {
          background-color: transparent;
          border: 0;
          border-radius: 8px;
          color: ${theme.textColorLighter};
          cursor: pointer;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 700;
          outline: 0;
          padding: 7px 10px;
          transition: background-color 0.3s, color 0.3s;
        }
        .mode-button:hover,
        .mode-button.active {
          background-color: ${theme.lightGrey};
          color: ${theme.textColor};
        }
        .theme-selector {
          padding: 10px 15px;
        }
        .content {
          border-top: 1px solid ${theme.lightGrey};
          flex-grow: 1;
          flex-shrink: 1;
          overflow: hidden;
        }
        .content :global(.colorpicker) {
          min-width: 225px;
        }
        .content :global(.colorpicker .picker) {
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default SiteSettingsThemesEditor;
